/**
 * 檔案用途區塊
 * @module locale.helper
 * @purpose 提供 LocaleDash 語系字串的驗證、轉換與預設值處理工具，供 URL lang 參數解析與語系合法性判斷使用。
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
import { LocaleDash } from '@shared/enums';

/**
 * 宣告內容用途說明與單號關聯
 * @description 從輸入字串比對 LocaleDash 列舉值，成功時回傳對應 enum，失敗時回傳 null。
 * @purpose 統一 URL 或外部輸入語系值的正規化與型別安全轉換入口。
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
export function parseLocaleFromString(lang: string | null): LocaleDash | null {
  if (!lang) {
    return null;
  }

  // 將字串轉換為小寫並比對 LocaleDash enum 的值
  const normalizedLang = lang.toLowerCase().trim();

  // 檢查是否為有效的 LocaleDash 值
  const validLocale = Object.values(LocaleDash).find((locale) => locale.toLowerCase().trim() === normalizedLang);

  return validLocale || null;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 將語系字串轉換為 LocaleDash；當輸入無效時回傳指定預設語系。
 * @purpose 在需要保底語系的流程中避免 null 判斷分支，確保可用語系值。
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
export function parseLocaleFromStringWithDefault(
  lang: string | null,
  defaultValue: LocaleDash = LocaleDash.en_US,
): LocaleDash {
  return parseLocaleFromString(lang) || defaultValue;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 判斷輸入字串是否可被解析為有效的 LocaleDash 列舉值。
 * @purpose 提供布林型檢核介面，便於表單驗證或路由參數前置判斷。
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
export function isValidLocale(lang: string | null): boolean {
  return parseLocaleFromString(lang) !== null;
}

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:22:21.800Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note 已僅調整註解並套用三段式結構：新增檔案用途區塊、三個函式的宣告用途說明與 Jira 關聯，以及底部 llm 分析紀錄區；所有 @external 皆為 https://innotech.atlassian.net/browse/IN-113493，程式邏輯未變更。
 */
